import { Button, TableColumnsType } from 'antd';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { EDIT_FUNCTIONARY } from '../../../shared/Routes';
import { NavigateOptions } from 'react-router-dom';
import {
	formatFunctionaryType,
	getDisplayGender,
	hasPrivilege,
} from '../../../shared/Utils';
import { PrivilegeCodes } from '../../../shared/RoleConstants';
import useUserSession from '../../../hooks/useUserSession';
import { PositionAssignmentFilters } from './FilterFunctionaries';
import { PositionAssignmentUser } from '../../../shared/MySrcm';
import { UserRole } from '../../../shared/VolunteerApplicationServiceTypes';
import { ADD_FUNCTIONARY_UI_CONFIG } from '../../../shared/Constants';

interface FunctionaryTableColumnsProps {
	navigateWithFunctionarySrcmId: (
		route: string,
		functionarySIdParam?: string,
		params?: NavigateOptions
	) => void;
	onFilterChange: (filters: PositionAssignmentFilters) => void;
}

export const FunctionaryTableColumns = ({
	navigateWithFunctionarySrcmId,
}: FunctionaryTableColumnsProps): TableColumnsType<PositionAssignmentUser> => {
	const handleEditProposerClick = useCallback(
		(functionary: PositionAssignmentUser) => {
			navigateWithFunctionarySrcmId(EDIT_FUNCTIONARY, functionary.ref, {
				state: { from: 'Edit Functionary/Proposer' },
			});
		},
		[navigateWithFunctionarySrcmId]
	);
	const { t } = useTranslation();
	const { loggedInUserPrivileges } = useUserSession();
	const shouldHideHomeMappings = ADD_FUNCTIONARY_UI_CONFIG.hideHomeMappings;

	const showEditFunctionary = hasPrivilege(
		PrivilegeCodes.CREATE_OR_UPDATE_POSITION_ASSIGNMENT,
		loggedInUserPrivileges
	);

	const homeZoneColumn = shouldHideHomeMappings
		? []
		: [
				{
					title: t('homeZone'),
					dataIndex: 'srcm_group',
					key: 'homeZone',
					render: (_: string, record: PositionAssignmentUser) =>
						record.srcm_group?.name || '-',
				},
		  ];

	const columns: TableColumnsType<PositionAssignmentUser> = [
		{
			title: t('name'),
			dataIndex: 'name',
			key: 'name',
		},
		{
			title: t('email'),
			dataIndex: 'email',
			key: 'email',
		},
		{
			title: t('gender'),
			dataIndex: 'gender',
			key: 'gender',
			render: (gender: string) => getDisplayGender(gender),
		},
		{
			title: t('mobile'),
			dataIndex: 'mobile',
			key: 'mobile',
		},
		{
			title: t('SRCMID'),
			dataIndex: 'ref',
			key: 'ref',
		},
		...homeZoneColumn,
		{
			title: t('functionaryType'),
			key: 'functionaryType',
			render: (_, record) => {
				if (!record.assignments?.length) return '-';

				const functionaryTypes = Array.from(
					new Set(
						record.assignments
							.filter((a) => a.assign_active)
							.map((a) => a.position?.code)
							.filter(
								(code): code is string =>
									Boolean(code) && code !== UserRole.APP_ROLE_ADMIN
							)
					)
				).map(formatFunctionaryType);

				return functionaryTypes.length ? functionaryTypes.join(', ') : '-';
			},
		},
	];

	if (showEditFunctionary) {
		columns.push({
			title: t('action'),
			key: 'operation',
			fixed: 'right',
			width: 150,
			render: (__, record) => (
				<Button
					className={`hidden sm:block font-sans font-bold text-sm border border-solid mt-0.5 text-primary-blue border-primary-blue w-[129px]`}
					variant="outlined"
					onClick={() => handleEditProposerClick(record)}
				>
					{t('editOrUpdate')}
				</Button>
			),
		});
	}

	return columns;
};
